// The "Several awards" maths for the glass award form (quaich-form.js): parsing a pasted list, composing each award's
// engraving text, checking it, pricing it and turning one add-to-cart form into one cart line per award.
// No DOM in here, so reports/2026-10/41-glass-award-form/batch.test.js can run it under node.
(() => {
  const NAME_TO_FOLLOW = '[Name to follow]';
  const MAX_QTY = 20;
  const DEFAULT_LIMITS = { maxLines: 8, maxChars: 500, maxRows: 60 };
  const AWARD_WORDS = /\b(?:of the year|awards?|winners?|champions?|best|players?|most|top|runners?|places?|service|prizes?|trophy|trophies|cups?|teams?)\b/i;
  const NAME_UNKNOWN = /^(?:tbc|tba|to be confirmed|to follow|name to follow|\[name to follow\])$/i;
  const HEADER = [/^(?:awards?|titles?|categor(?:y|ies))$/i, /^(?:names?|winners?|recipients?)$/i];
  const BULLET = /^(?:[•*·▪●]\s*|[-–—]\s+)/;
  const NUMBERING = /^(?:#\s*\d{1,3}|\d{1,3}[.)])(?:\s+|$)/;
  // "x2", "× 2", "(x2)", "x 2" at the end, but not the x in "Max 2"
  const QTY_AT_END = /(^|[^A-Za-z0-9])[(\[]?\s*[x×]\s*(\d{1,2})\s*[)\]]?\s*$/i;
  const SEPARATORS = [/\s+[-–—]\s+|\s*[–—]\s*/, /\s*:\s*/, /\s*,\s*/];
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const clean = (value) => String(value == null ? '' : value).replace(/\s+/g, ' ').trim();
  const qtyOf = (row) => {
    const quantity = Math.floor(Number(row && row.qty));
    return quantity >= 1 ? Math.min(quantity, MAX_QTY) : 1;
  };
  const isFilled = (row) => Boolean(row && (clean(row.award) || clean(row.name) || row.tbc));
  const activeRows = (rows) => rows.filter(isFilled);
  const looksLikeAward = (text) => AWARD_WORDS.test(text);

  const toRow = (award, name, qty) => {
    const tbc = NAME_UNKNOWN.test(name);
    return { award, name: tbc ? '' : name, qty, tbc };
  };

  const parseLine = (raw) => {
    let line = String(raw).trim();
    if (!line) return null;
    line = line.replace(BULLET, '').replace(NUMBERING, '').trim();
    let qty = 1;
    const quantity = QTY_AT_END.exec(line);
    if (quantity) {
      qty = Math.min(Math.max(parseInt(quantity[2], 10), 1), MAX_QTY);
      line = line.slice(0, quantity.index + quantity[1].length).replace(/[\s,\-–—:]+$/, '');
    }

    if (line.includes('\t')) {
      let cells = line.split('\t').map(clean);
      if (cells.length >= 3 && /^\d{1,3}$/.test(cells[0])) cells = cells.slice(1);
      if (cells.length >= 3 && !quantity && /^\d{1,2}$/.test(cells[cells.length - 1])) qty = Math.min(Math.max(parseInt(cells[cells.length - 1], 10), 1), MAX_QTY);
      if (HEADER[0].test(cells[0]) && HEADER[1].test(cells[1] || '')) return null;
      if (cells.length > 1) return toRow(cells[0], cells[1], qty);
      line = cells[0];
    }

    line = clean(line);
    if (!line) return null;
    let split = null;
    SEPARATORS.forEach((pattern) => {
      const found = pattern.exec(line);
      if (found && found[0] && (!split || found.index < split.index)) split = found;
    });
    if (split) return toRow(clean(line.slice(0, split.index)), clean(line.slice(split.index + split[0].length)), qty);
    return looksLikeAward(line) ? toRow(line, '', qty) : toRow('', line, qty);
  };

  // One award per line from a spreadsheet, Word or an email; award first, then the name.
  const parseList = (text) =>
    String(text || '')
      .split(/\r\n|\r|\n/)
      .map(parseLine)
      .filter(isFilled);

  // shared: [{ type: 'text', value } | { type: 'award' } | { type: 'name' }] in engraving order
  const composeLines = (shared, row) => {
    const lines = [];
    shared.forEach((item) => {
      const value = item.type === 'award' ? clean(row.award) : item.type === 'name' ? (row.tbc ? NAME_TO_FOLLOW : clean(row.name)) : clean(item.value);
      if (value) lines.push(value);
    });
    return lines;
  };
  const compose = (shared, row) => composeLines(shared, row).join('\n');

  // Errors come back in table order so the first one is the row to focus; row is the 0-based table row (null for the shared lines)
  // limits.colourSome: a colour is chosen for only some awards, so at least one row must be ticked
  const validate = (shared, rows, limits = {}) => {
    const { maxLines, maxChars, maxRows, colourSome } = { ...DEFAULT_LIMITS, ...limits };
    const errors = [];
    const active = [];
    rows.forEach((row, index) => isFilled(row) && active.push(index));
    const hasName = shared.some((item) => item.type === 'name');
    if (!active.length) errors.push({ row: 0, field: 'award', message: 'Add at least one award to the list' });
    if (active.length > maxRows) errors.push({ row: active[maxRows], field: 'award', message: `Up to ${maxRows} awards can be ordered here` });

    active.forEach((index) => {
      const row = rows[index];
      const number = index + 1;
      if (hasName && !row.tbc && !clean(row.name)) {
        errors.push({ row: index, field: 'name', message: `Add a name for award ${number}, or tick Not known yet` });
        return;
      }
      const text = compose(shared, row);
      const count = composeLines(shared, row).length;
      if (count > maxLines) errors.push({ row: index, field: 'award', message: `Award ${number} has ${count} lines — up to ${maxLines} fit` });
      else if (text.length > maxChars) errors.push({ row: index, field: 'award', message: `Award ${number}'s wording is too long (${maxChars} characters max)` });
    });
    if (colourSome && active.length && !active.some((index) => rows[index].colour)) {
      errors.push({ row: active[0], field: 'colour', message: 'Tick the awards you want coloured, or choose Every award' });
    }
    errors.sort((a, b) => (a.row ?? -1) - (b.row ?? -1));
    return { ok: errors.length === 0, errors, active };
  };

  // All amounts in pence. The colour (infill) is charged per coloured award, on every award or only the ticked rows
  // (colourAll false); the logo set-up fee is charged once per order, not per award. unit excludes the colour.
  const totals = (rows, { base = 0, engraving = 0, infill = 0, logo = false, logoFee = 0, colourAll = true } = {}) => {
    const active = activeRows(rows);
    const awards = active.reduce((sum, row) => sum + qtyOf(row), 0);
    const coloured = infill ? active.filter((row) => colourAll || row.colour).reduce((sum, row) => sum + qtyOf(row), 0) : 0;
    const unit = base + engraving;
    const logoTotal = logo && awards > 0 ? logoFee : 0;
    return { rows: active.length, awards, base, engraving, unit, infill, coloured, colour: coloured * infill, logo: logoTotal, total: awards * unit + coloured * infill + logoTotal };
  };

  // An award left uncoloured: the app's Infill answer becomes No and its infill add-on leaves the bundle, so Shopify
  // prices that line without the colour (checked on the store: £58 coloured, £48 not)
  const withoutColour = (item) => {
    if (!item.Infill || item.Infill === 'No') return;
    item.Infill = 'No';
    try {
      const bundles = JSON.parse(item._io_product_bundle_ids);
      if (Array.isArray(bundles)) item._io_product_bundle_ids = JSON.stringify(bundles.filter((bundle) => bundle && bundle.name !== 'Infill'));
    } catch (error) {
      /* An unrecognised app value stays as it was */
    }
    try {
      const options = JSON.parse(item._io_properties);
      if (options && typeof options === 'object' && Object.prototype.hasOwnProperty.call(options, 'Infill')) {
        options.Infill = 'No';
        item._io_properties = JSON.stringify(options);
      }
    } catch (error) {
      /* An unrecognised app value stays as it was */
    }
  };

  // entries: the add-to-cart form's [name, value] pairs. One cart line per filled row, copying every property the
  // Infinite Options app set; newest-first carts read award 1 first when the list is sent reversed, so it is returned reversed.
  const buildItems = (entries, rows, { shared, neededBy = '', nameLarger = false, colourAll = true } = {}) => {
    const properties = {};
    let id = null;
    [...entries].forEach(([key, value]) => {
      const property = /^properties\[(.*)\]$/.exec(key);
      if (property) properties[property[1]] = String(value);
      else if (key === 'id') id = /^\d+$/.test(String(value)) ? Number(value) : value;
    });
    const active = activeRows(rows);
    // "Award" counts physical awards, so the engraver sees "2–3 of 7" on a line of two
    const count = active.reduce((sum, row) => sum + qtyOf(row), 0);
    let next = 1;
    const items = active.map((row, index) => {
      const quantity = qtyOf(row);
      const from = next;
      next += quantity;
      const text = compose(shared, row);
      const item = { ...properties, Text: text };
      if (typeof item._io_properties === 'string') {
        try {
          const options = JSON.parse(item._io_properties);
          if (options && typeof options === 'object' && Object.prototype.hasOwnProperty.call(options, 'Text')) {
            options.Text = text;
            item._io_properties = JSON.stringify(options);
          }
        } catch (error) {
          /* An unrecognised app value stays as it was */
        }
      }
      if (!colourAll && !row.colour) withoutColour(item);
      if (item._io_order_group) item._io_order_group = `${item._io_order_group}-${index + 1}`;
      item.Award = quantity > 1 ? `${from}–${from + quantity - 1} of ${count}` : `${from} of ${count}`;
      if (neededBy) item['Needed by'] = neededBy;
      if (nameLarger) item.Layout = 'Name larger than the rest';
      else delete item.Layout;
      return { id, quantity, properties: item };
    });
    return items.reverse();
  };

  // "£48.00", "£1,234.00" from pence
  const formatMoney = (pence) => {
    const value = Math.round(Number(pence) || 0);
    const whole = String(Math.floor(Math.abs(value) / 100)).replace(/\B(?=(\d{3})+$)/g, ',');
    return `${value < 0 ? '-' : ''}£${whole}.${String(Math.abs(value) % 100).padStart(2, '0')}`;
  };

  // "Antique Gold (+£10)" -> 1000; 0 for "No" and anything without a price
  const parseAddOn = (value) => {
    const match = /\+\s*£\s*(\d+(?:,\d{3})*(?:\.\d{1,2})?)/.exec(String(value || ''));
    return match ? Math.round(parseFloat(match[1].replace(/,/g, '')) * 100) : 0;
  };

  // "2026-11-13" -> "Fri 13 Nov 2026" (calendar maths in UTC so the day never shifts with the visitor's time zone)
  const formatNeededBy = (iso) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    if (!match) return '';
    const date = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3]));
    if (date.getUTCMonth() !== +match[2] - 1) return '';
    return `${WEEKDAYS[date.getUTCDay()]} ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
  };

  const api = { parseList, compose, composeLines, validate, totals, buildItems, formatMoney, parseAddOn, formatNeededBy, activeRows, isFilled, qtyOf, looksLikeAward, NAME_TO_FOLLOW, MAX_QTY };
  if (typeof window !== 'undefined') window.WalAwardBatch = api;
  if (typeof module !== 'undefined') module.exports = api;
})();
